import { useCallback, useEffect, useState } from "react";
import { getApiService } from "../services/api";
import type { ApiStock } from "../services/api";
import { InfiniteScroll } from "../components/InfiniteScroll";
import { KlineChart } from "../components/KlineChart";
import type { ChartMarker } from "../components/KlineChart";
import { HoldingDialog } from "../components/HoldingDialog";
import { tdCounts } from "../services/indicators";
import { fmtNum, fmtPercent, percentClass, exchangeUrl } from "../services/format";
import { useSearchParams, updateSearchParams } from "../hooks/useRoute";

const PAGE_SIZE = 10;

type SubTab = "dk" | "updown";

// 通用信号列表项：K线图（带标记）、信号日期/价格、最大涨幅/累计涨幅、交易所、添加持仓
function SignalItem({
  stock,
  markers,
  signalLabel,
  onAdd,
}: {
  stock: ApiStock;
  markers: ChartMarker[];
  signalLabel: string;
  onAdd: (s: ApiStock) => void;
}) {
  const url = exchangeUrl(stock.exchange, stock.code);
  return (
    <div className="item-card mb-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-semibold">{stock.name}</span>
          <span className="flat text-12px">
            {stock.exchange}
            {stock.code}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {url && (
            <a className="flat text-12px" href={url} target="_blank" rel="noreferrer">
              交易所 ↗
            </a>
          )}
          <button className="btn btn-sm" onClick={() => onAdd(stock)}>
            + 添加持仓
          </button>
        </div>
      </div>

      <KlineChart klines={stock.klines ?? []} markers={markers} height={140} />

      {/* 手机：信号日期/信号价/最大涨幅/累计涨幅 四列平分宽度，标签行+数值行对齐，内容垂直居中；PC：横排 */}
      <div className="mt-2 text-13px grid grid-cols-4 items-center gap-y-1 sm:flex sm:items-center sm:justify-between sm:gap-3">
        <div className="flat sm:flex sm:items-center sm:gap-1">
          <div>{signalLabel}</div>
          <div className="up font-semibold">{(stock.signalDate ?? "").slice(5)}</div>
        </div>
        <div className="flat sm:flex sm:items-center sm:gap-1">
          <div>信号价</div>
          <div className="font-semibold">{fmtNum(stock.signalPrice)}</div>
        </div>
        <div className="flat sm:flex sm:items-center sm:gap-1">
          <div>最大涨幅</div>
          <div className={`font-semibold ${percentClass(stock.maxPercent)}`}>{fmtPercent(stock.maxPercent == null ? null : stock.maxPercent / 100)}</div>
        </div>
        <div className="flat sm:flex sm:items-center sm:gap-1">
          <div>累计涨幅</div>
          <div className={`font-semibold ${percentClass(stock.totalPercent)}`}>{fmtPercent(stock.totalPercent == null ? null : stock.totalPercent / 100)}</div>
        </div>
      </div>
    </div>
  );
}

// 多空信号视图
function DKView({ onAdd }: { onAdd: (s: ApiStock) => void }) {
  const api = getApiService();
  // 筛选状态跟随 URL 查询参数（?indicator=&trend=&scope=），刷新保持
  const params = useSearchParams();
  const rawIndicator = params.get("indicator");
  const indicator: "MACD" | "KDJ" | "RSI" = rawIndicator === "KDJ" || rawIndicator === "RSI" ? rawIndicator : "MACD";
  const trend: "duo" | "kong" = params.get("trend") === "kong" ? "kong" : "duo";
  const scope: "all" | "favorites" = params.get("scope") === "favorites" ? "favorites" : "all";
  const setIndicator = (v: "MACD" | "KDJ" | "RSI") => updateSearchParams({ indicator: v });
  const setTrend = (v: "duo" | "kong") => updateSearchParams({ trend: v });
  const setScope = (v: "all" | "favorites") => updateSearchParams({ scope: v });
  const [stocks, setStocks] = useState<ApiStock[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadPage = useCallback(
    async (p: number, replace = false) => {
      setLoading(true);
      try {
        const res = await api.getDKStocks(trend, scope, indicator, p, PAGE_SIZE);
        const list = res.data?.stocks ?? [];
        // 空页兜底：追加页没有数据时立即停止后续加载
        if (!replace && list.length === 0) {
          setTotal(0);
          return;
        }
        setTotal(res.data?.pagination?.total ?? 0);
        setStocks((prev) => (replace ? list : [...prev, ...list]));
        setPage(p); // 记录已加载页码，避免重复请求同一页
      } catch {
        // 401 等错误由 AuthContext 统一处理
      } finally {
        setLoading(false);
      }
    },
    [api, trend, scope, indicator],
  );

  useEffect(() => {
    setPage(1);
    loadPage(1, true);
  }, [loadPage]);

  return (
    <div>
      {/* 筛选：手机端每组垂直排列，宽屏同行 */}
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className="flex flex-wrap gap-2">
          {(["MACD", "KDJ", "RSI"] as const).map((v) => (
            <button
              key={v}
              className={`chip ${indicator === v ? "chip-active" : ""}`}
              onClick={() => setIndicator(v)}
            >
              {v}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {(
            [
              { v: "duo", label: "多（D）" },
              { v: "kong", label: "空（K）" },
            ] as const
          ).map((o) => (
            <button
              key={o.v}
              className={`chip ${trend === o.v ? "chip-active" : ""}`}
              onClick={() => setTrend(o.v)}
            >
              {o.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {(
            [
              { v: "all", label: "全部" },
              { v: "favorites", label: "持仓" },
            ] as const
          ).map((o) => (
            <button
              key={o.v}
              className={`chip ${scope === o.v ? "chip-active" : ""}`}
              onClick={() => setScope(o.v)}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {stocks.length === 0 && !loading ? (
        <div className="flat text-center py-10">暂无{trend === "duo" ? "看多" : "看空"}信号股票</div>
      ) : (
        <InfiniteScroll hasMore={stocks.length < total} loading={loading} onLoadMore={() => loadPage(page + 1)}>
          {stocks.map((s) => {
            const idx = (s.klines ?? []).findIndex((k) => k.date === s.signalDate);
            const markers: ChartMarker[] =
              idx >= 0
                ? [
                    {
                      index: idx,
                      text: trend === "duo" ? "D" : "K",
                      position: trend === "duo" ? "up" : "down",
                      kind: "signal",
                    },
                  ]
                : [];
            return (
              <SignalItem
                key={s.id}
                stock={s}
                markers={markers}
                signalLabel={trend === "duo" ? "信号日期 " : "信号日期 "}
                onAdd={onAdd}
              />
            );
          })}
        </InfiniteScroll>
      )}
    </div>
  );
}

// 神奇9转视图
function UpdownView({ onAdd }: { onAdd: (s: ApiStock) => void }) {
  const api = getApiService();
  // 筛选状态跟随 URL 查询参数（?trend=&scope=），刷新保持
  const params = useSearchParams();
  const trend: "up" | "down" = params.get("trend") === "up" ? "up" : "down";
  const scope: "all" | "favorites" = params.get("scope") === "favorites" ? "favorites" : "all";
  const setTrend = (v: "up" | "down") => updateSearchParams({ trend: v });
  const setScope = (v: "all" | "favorites") => updateSearchParams({ scope: v });
  const [stocks, setStocks] = useState<ApiStock[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadPage = useCallback(
    async (p: number, replace = false) => {
      setLoading(true);
      try {
        const res = await api.getUpdownStocks(9, trend, scope, p, PAGE_SIZE);
        const list = res.data?.stocks ?? [];
        // 空页兜底：追加页没有数据时立即停止后续加载
        if (!replace && list.length === 0) {
          setTotal(0);
          return;
        }
        setTotal(res.data?.pagination?.total ?? 0);
        setStocks((prev) => (replace ? list : [...prev, ...list]));
        setPage(p); // 记录已加载页码，避免重复请求同一页
      } catch {
        // 401 等错误由 AuthContext 统一处理
      } finally {
        setLoading(false);
      }
    },
    [api, trend, scope],
  );

  useEffect(() => {
    setPage(1);
    loadPage(1, true);
  }, [loadPage]);

  const trendLabel = trend === "down" ? "底部机会（下跌9转）" : "顶部风险（上涨9转）";

  return (
    <div>
      {/* 筛选：手机端每组垂直排列，宽屏同行 */}
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className="flex flex-wrap gap-2">
          {(
            [
              { v: "down", label: "底部机会" },
              { v: "up", label: "顶部风险" },
            ] as const
          ).map((o) => (
            <button
              key={o.v}
              className={`chip ${trend === o.v ? "chip-active" : ""}`}
              onClick={() => setTrend(o.v)}
            >
              {o.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {(
            [
              { v: "all", label: "全部" },
              { v: "favorites", label: "持仓" },
            ] as const
          ).map((o) => (
            <button
              key={o.v}
              className={`chip ${scope === o.v ? "chip-active" : ""}`}
              onClick={() => setScope(o.v)}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {stocks.length === 0 && !loading ? (
        <div className="flat text-center py-10">暂无{trendLabel}股票</div>
      ) : (
        <InfiniteScroll hasMore={stocks.length < total} loading={loading} onLoadMore={() => loadPage(page + 1)}>
          {stocks.map((s) => {
            const closes = (s.klines ?? []).map((k) => k.close);
            const counts = tdCounts(closes);
            // 只展示所选方向的9转数字：底部机会只看下跌计数（蜡烛下方），顶部风险只看上涨计数（蜡烛上方）
            const markers: ChartMarker[] = counts
              .map((c, i): ChartMarker | null => {
                if (trend === "down" && c < 0) {
                  return { index: i, text: String(-c), position: "down", kind: "count" };
                }
                if (trend === "up" && c > 0) {
                  return { index: i, text: String(c), position: "up", kind: "count" };
                }
                return null;
              })
              .filter((m): m is ChartMarker => m != null);
            // 信号日期标注：上涨9转在蜡烛上方（▲）、下跌9转在蜡烛下方（▼），避开蜡烛本体
            const signalIdx = (s.klines ?? []).findIndex((k) => k.date === s.signalDate);
            if (signalIdx >= 0 && s.signalDate) {
              markers.push({
                index: signalIdx,
                text: s.signalDate.slice(5),
                position: trend === "up" ? "up" : "down",
                kind: "signal9",
              });
            }
            return (
              <SignalItem key={s.id} stock={s} markers={markers} signalLabel="信号日期 " onAdd={onAdd} />
            );
          })}
        </InfiniteScroll>
      )}
    </div>
  );
}

// 股票 Tab
export function StocksTab() {
  const api = getApiService();
  // 子视图从 URL 查询参数 ?tab=dk|updown 读取，刷新保持
  const params = useSearchParams();
  const subTab: SubTab = params.get("tab") === "updown" ? "updown" : "dk";
  const [holdingStock, setHoldingStock] = useState<ApiStock | null>(null);
  const [toast, setToast] = useState("");

  // 切换子视图时重置该视图的筛选参数（干净的 URL）
  const switchSubTab = (t: SubTab) => {
    if (t === "dk") {
      updateSearchParams({ tab: "dk", indicator: "MACD", trend: "duo", scope: "all" });
    } else {
      // 清除多空信号专属参数 indicator
      updateSearchParams({ tab: "updown", trend: "down", scope: "all", indicator: null });
    }
  };

  const confirmHolding = async (quantity: number, price: number) => {
    if (!holdingStock) return;
    const res = await api.addToFavorites(holdingStock.id!, price, quantity);
    if (res.code === 200) {
      setToast("已添加到持仓");
      setHoldingStock(null);
    } else {
      setToast(res.message ?? "添加失败");
    }
    setTimeout(() => setToast(""), 2000);
  };

  return (
    <div className="pt-2">
      <div className="flex gap-5 mb-3 border-b" style={{ borderColor: "var(--border)" }}>
        <button className={`tab ${subTab === "dk" ? "tab-active" : ""}`} onClick={() => switchSubTab("dk")}>
          多 / 空信号
        </button>
        <button className={`tab ${subTab === "updown" ? "tab-active" : ""}`} onClick={() => switchSubTab("updown")}>
          神奇 9 转
        </button>
      </div>

      {subTab === "dk" ? <DKView onAdd={setHoldingStock} /> : <UpdownView onAdd={setHoldingStock} />}

      <HoldingDialog
        open={!!holdingStock}
        stock={holdingStock}
        onClose={() => setHoldingStock(null)}
        onConfirm={confirmHolding}
      />

      {toast && (
        <div
          className="fixed left-1/2 -translate-x-1/2 bottom-8 px-4 py-2 rounded-full text-13px"
          style={{ background: "var(--chart-tooltip)", color: "var(--chart-tooltip-fg)", zIndex: 200 }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}
